import type { ReactNode } from "react";
import { EmptyState, SegmentedControl, Spinner, Switch } from "@cntrl/ui";
import { useIsPro } from "../../app/bandContext.js";
import { useTeams } from "../../app/hooks/useTeams.js";
import { useSaveTeamSettings, useTeamSettings, type ManagerMode } from "../../app/hooks/useTeamSettings.js";
import styles from "./SettingsTeamTab.module.scss";

const MODE_OPTIONS = [
  { value: "hands_on", label: "Hands on" },
  { value: "hands_off", label: "Hands off" },
] as const;

/**
 * Settings › Team (ART-353) — the band's choices about its partners. Saves on change; there is no Save button.
 *
 * YOUR TEAM: Manager mode, Label can see release budgets, Booking discovery (shown, off and locked, COMING SOON).
 * PRO: Advance Docs Pro, on Pro bands only.
 *
 * Each row is its name on the left and its control on the right, with a STATUS line under the control saying what
 * the current setting means — naming the linked manager or label, so it reads as a fact about them ("Fernwood
 * Records can't see the budget for releases") rather than a description of the switch. A row with nobody to apply
 * to (no manager / no label linked) has its control greyed out and inert, and the status says why. The server
 * refuses those writes too; the grey-out is so nobody presses a control that can only be refused.
 */
export function SettingsTeamTab() {
  const settings = useTeamSettings();
  const save = useSaveTeamSettings();
  const teams = useTeams();
  const isPro = useIsPro();

  if (settings.isPending) return <Spinner label="Loading team settings" />;
  if (settings.isError || !settings.data) {
    return <EmptyState title="Couldn't load team settings" description="Try again in a moment." />;
  }

  // The generated shape marks every field optional; normalise once so the rows read plain booleans.
  const s = {
    hasManager: settings.data.hasManager === true,
    hasLabel: settings.data.hasLabel === true,
    labelSeesBudgets: settings.data.labelSeesBudgets === true,
    advanceDocsPro: settings.data.advanceDocsPro !== false,
  };
  const mode: ManagerMode = settings.data.managerMode === "hands_off" ? "hands_off" : "hands_on";

  // The settings read is the authority for WHETHER a manager/label is linked (it is what the server checks on save);
  // the Teams list only supplies the NAME the status line uses, so a list still loading reads "Your manager".
  const linked = (teams.data?.items ?? []).filter((t) => t.status === "active" || t.status === "linked");
  const managerName = linked.find((t) => t.linkType === "management")?.orgName || "Your manager";
  const labelName = linked.find((t) => t.linkType === "label")?.orgName || "Your label";

  return (
    <div className={styles.tab}>
      <section className={styles.group} aria-labelledby="team-yours">
        <h3 id="team-yours" className={styles.groupTitle}>
          Your team
        </h3>
        <div className={styles.card}>
          <SettingRow
            name="Manager mode"
            inert={!s.hasManager}
            status={
              !s.hasManager
                ? "Connect a manager to use this."
                : mode === "hands_on"
                  ? `You get the same show notifications as ${managerName}.`
                  : `${managerName} handles show notifications. You only hear about what needs you.`
            }
          >
            <SegmentedControl
              label="Manager mode"
              value={mode}
              options={MODE_OPTIONS}
              onChange={(next) => {
                if (s.hasManager && next !== mode) save.mutate({ managerMode: next });
              }}
            />
          </SettingRow>

          <SettingRow
            name="Label can see release budgets"
            inert={!s.hasLabel}
            status={
              !s.hasLabel
                ? "Connect a label to use this."
                : s.labelSeesBudgets
                  ? `${labelName} can see the budget for releases.`
                  : `${labelName} can't see the budget for releases.`
            }
          >
            <Switch
              checked={s.hasLabel && s.labelSeesBudgets}
              onChange={(next) => save.mutate({ labelSeesBudgets: next })}
              label="Label can see release budgets"
              disabled={!s.hasLabel}
            />
          </SettingRow>

          {/* COMING SOON (PO, 2026-10-29): shown, off and locked. It reads nothing and writes nothing, so the band's
              stored open_to_support is left exactly as it was. */}
          <SettingRow
            name={
              <>
                Booking discovery <span className={styles.soon}>(Coming soon)</span>
              </>
            }
            inert
            status="Booking agents can't find your band yet."
          >
            <Switch checked={false} onChange={() => {}} label="Booking discovery (coming soon)" disabled />
          </SettingRow>
        </div>
      </section>

      {isPro && (
        <section className={styles.group} aria-labelledby="team-pro">
          <h3 id="team-pro" className={styles.groupTitle}>
            Pro
          </h3>
          <div className={styles.card}>
            <SettingRow
              name="Advance Docs Pro"
              status={
                s.advanceDocsPro ? "Your team uses the Pro advance docs." : "Your team uses the standard advance docs."
              }
            >
              <Switch
                checked={s.advanceDocsPro}
                onChange={(next) => save.mutate({ advanceDocsPro: next })}
                label="Advance Docs Pro"
              />
            </SettingRow>
          </div>
        </section>
      )}
    </div>
  );
}

/** One setting: its name on the left; its control on the right with the status line under it. `inert` greys the
 * control out (and only the control — the status stays readable, because it is the reason). */
function SettingRow({
  name,
  status,
  inert,
  children,
}: {
  name: ReactNode;
  status: string;
  inert?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={styles.item}>
      <span className={styles.label}>{name}</span>
      <div className={styles.side}>
        <div className={styles.control} data-disabled={inert || undefined}>
          {children}
        </div>
        <span className={styles.status}>{status}</span>
      </div>
    </div>
  );
}
